import React from "react";
import { Link } from "react-router-dom";

// Port of web/src/components/ui/StampButton.jsx — same visual spec (CLAUDE.md §27).
// Single source of truth for the stamp-style pill in the admin app.

type StampAs = "button" | "div" | "link";

interface StampButtonProps {
  label: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
  as?: StampAs;
  to?: string;
  className?: string;
  type?: "button" | "submit" | "reset";
  ariaLabel?: string;
  disabled?: boolean;
  variant?: "primary" | "danger";
}

const baseStyle = (variant: "primary" | "danger"): React.CSSProperties => ({
  background: variant === "danger" ? "#B51D28" : "#16729A",
  color: "#FFFFFF",
  border: "2px solid #000000",
  boxShadow: "4px 4px 0px #000000",
  padding: "10px 25px",
  fontFamily: "'Inter', sans-serif",
  fontWeight: 500,
  fontSize: "25px",
  lineHeight: "30px",
});

const StampButton: React.FC<StampButtonProps> = ({
  label,
  icon,
  onClick,
  as = "button",
  to,
  className = "",
  type = "button",
  ariaLabel,
  disabled,
  variant = "primary",
}) => {
  const inner = (
    <span className="inline-flex items-center gap-1.5">
      <span>{label}</span>
      {icon}
    </span>
  );
  const baseClass =
    "inline-flex items-center justify-center select-none " + className;
  const style = baseStyle(variant);

  if (as === "div") {
    return (
      <div className={baseClass} style={style} aria-label={ariaLabel}>
        {inner}
      </div>
    );
  }
  if (as === "link" && to) {
    return (
      <Link to={to} className={baseClass + " no-underline"} style={style} aria-label={ariaLabel}>
        {inner}
      </Link>
    );
  }
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={baseClass + " hover:opacity-95 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"}
      style={style}
      aria-label={ariaLabel}
    >
      {inner}
    </button>
  );
};

export default StampButton;
